/**
 * Canonical request/response examples rendered by the Help MDX articles.
 *
 * The MDX source stays editorial and human-readable, while the runnable API
 * examples live here in a typed registry that can be reused by shared
 * documentation components.
 */
export type ApiReferenceExampleId =
  | 'metaTenant'
  | 'metaScopes'
  | 'campaignsList'
  | 'campaignDetail'
  | 'callsList'
  | 'callsCreate'
  | 'callDetail'
  | 'agentsList'
  | 'scriptsList'
  | 'usersList'
  | 'transferGroupsList';
export type ApiReferenceCodeLanguage = 'curl' | 'python' | 'php' | 'typescript';

export const API_REFERENCE_CODE_LANGUAGE_ORDER: ApiReferenceCodeLanguage[] = ['curl', 'python', 'php', 'typescript'];

export const API_REFERENCE_CODE_LANGUAGE_LABELS: Record<ApiReferenceCodeLanguage, string> = {
  curl: 'cURL',
  python: 'Python',
  php: 'PHP',
  typescript: 'TypeScript',
};

export const API_REFERENCE_CODE_LANGUAGE_MARKDOWN_FENCES: Record<ApiReferenceCodeLanguage, string> = {
  curl: 'bash',
  python: 'python',
  php: 'php',
  typescript: 'typescript',
};

export interface ApiReferenceExampleResponse {
  status: number;
  body: string;
}

export interface ApiReferenceExampleDefinition {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  fullUrl: string;
  examples: Partial<Record<ApiReferenceCodeLanguage, string>>;
  responses: ApiReferenceExampleResponse[];
}

export const DEFAULT_PUBLIC_API_BASE_URL = 'https://api.contact-tel.contactpro.com.br/public/v1';
export const DEFAULT_DEV_PUBLIC_API_BASE_URL = 'https://dev.api.contact-tel.contactpro.com.br/public/v1';
export const DEFAULT_APP_BASE_URL = 'https://contact-tel.contactpro.com.br';

/**
 * Remove trailing slashes so the examples always concatenate stable URLs.
 */
export function normalizePublicApiBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '');
}

/**
 * Resolve the canonical public API base URL for the current build/runtime.
 *
 * Priority:
 * 1. Explicit frontend env var.
 * 2. Hostname inference for dev and localhost contexts.
 * 3. Production default.
 */
export function resolvePublicApiBaseUrl(options?: {
  configuredBaseUrl?: string | null;
  hostname?: string | null;
}): string {
  const configuredBaseUrl = options?.configuredBaseUrl?.trim();
  if (configuredBaseUrl) {
    return normalizePublicApiBaseUrl(configuredBaseUrl);
  }

  const hostname = options?.hostname?.trim().toLowerCase() || '';
  if (
    hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname.startsWith('dev.')
  ) {
    return DEFAULT_DEV_PUBLIC_API_BASE_URL;
  }

  return DEFAULT_PUBLIC_API_BASE_URL;
}

const API_BASE_URL = resolvePublicApiBaseUrl({
  configuredBaseUrl: import.meta.env.VITE_PUBLIC_API_BASE_URL as string | undefined,
  hostname: typeof window !== 'undefined' ? window.location.hostname : undefined,
});
const APP_BASE_URL = DEFAULT_APP_BASE_URL;
const DEMO_TOKEN = 'ctt_live_01abc123def45678.secretValueGeneratedOnce';
const DEMO_IDEMPOTENCY_KEY = 'crm-lead-84921-attempt-1';

function buildAuthenticatedGetExamples(url: string): Partial<Record<ApiReferenceCodeLanguage, string>> {
  return {
    curl: [
      'curl --request GET \\',
      `  --url "${url}" \\`,
      `  --header "Authorization: Bearer ${DEMO_TOKEN}"`,
    ].join('\n'),
    python: [
      'import requests',
      '',
      `url = "${url}"`,
      'headers = {',
      `    "Authorization": "Bearer ${DEMO_TOKEN}",`,
      '}',
      '',
      'response = requests.get(url, headers=headers, timeout=30)',
      'response.raise_for_status()',
      'print(response.json())',
    ].join('\n'),
    php: [
      '<?php',
      '',
      `$ch = curl_init("${url}");`,
      'curl_setopt_array($ch, [',
      '    CURLOPT_RETURNTRANSFER => true,',
      '    CURLOPT_HTTPHEADER => [',
      `        "Authorization: Bearer ${DEMO_TOKEN}",`,
      '    ],',
      ']);',
      '',
      '$body = curl_exec($ch);',
      '$status = curl_getinfo($ch, CURLINFO_RESPONSE_CODE);',
      '',
      'if ($body === false) {',
      '    throw new RuntimeException(curl_error($ch));',
      '}',
      '',
      'curl_close($ch);',
      '',
      'if ($status >= 400) {',
      '    throw new RuntimeException($body);',
      '}',
      '',
      'echo $body;',
    ].join('\n'),
    typescript: [
      `const response = await fetch("${url}", {`,
      '  method: "GET",',
      '  headers: {',
      `    Authorization: "Bearer ${DEMO_TOKEN}",`,
      '  },',
      '});',
      '',
      'if (!response.ok) {',
      '  throw new Error(await response.text());',
      '}',
      '',
      'console.log(await response.json());',
    ].join('\n'),
  };
}

function buildCreateCallExamples(url: string): Partial<Record<ApiReferenceCodeLanguage, string>> {
  return {
    curl: [
      'curl --request POST \\',
      `  --url "${url}" \\`,
      `  --header "Authorization: Bearer ${DEMO_TOKEN}" \\`,
      `  --header "Idempotency-Key: ${DEMO_IDEMPOTENCY_KEY}" \\`,
      '  --header "Content-Type: application/json" \\',
      '  --data \'{',
      '    "contact": {',
      '      "identity": {',
      '        "kind": "external_id",',
      '        "value": "crm-contact-84921"',
      '      },',
      '      "name": "Mariana Souza",',
      '      "phones": [',
      '        { "number": "+5511999990001", "label": "primary" },',
      '        { "number": "+5511988880002", "label": "secondary" }',
      '      ]',
      '    },',
      '    "ai_agent_id": "f08c1745-2f11-4fb4-aab1-d120fb7251ce",',
      '    "client_reference": "operator:8472",',
      '    "dynamic_variables": {',
      '      "first_name": "Mariana",',
      '      "city": "Sao Paulo"',
      '    },',
      '    "pre_call_script_id": "d2c6a14e-77f9-46df-917c-53bd69c39701",',
      '    "script_ids": ["a1a97f62-9dc1-4ef4-8f2b-dbbd6d86c0b4"],',
      '    "retry_policy": {',
      '      "max_attempts": 3,',
      '      "delay_min_seconds": 30,',
      '      "delay_max_seconds": 120',
      '    },',
      '    "transfer_target": {',
      '      "type": "user",',
      '      "id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a"',
      '    },',
      '    "safety": {',
      '      "skip_if_called_today": true,',
      '      "skip_if_reached": true,',
      '      "skip_if_converted": true',
      '    }',
      '  }\'',
    ].join('\n'),
    python: [
      'import requests',
      '',
      `url = "${url}"`,
      'headers = {',
      `    "Authorization": "Bearer ${DEMO_TOKEN}",`,
      `    "Idempotency-Key": "${DEMO_IDEMPOTENCY_KEY}",`,
      '}',
      'payload = {',
      '    "contact": {',
      '        "identity": {',
      '            "kind": "external_id",',
      '            "value": "crm-contact-84921",',
      '        },',
      '        "name": "Mariana Souza",',
      '        "phones": [',
      '            {"number": "+5511999990001", "label": "primary"},',
      '            {"number": "+5511988880002", "label": "secondary"},',
      '        ],',
      '    },',
      '    "ai_agent_id": "f08c1745-2f11-4fb4-aab1-d120fb7251ce",',
      '    "client_reference": "operator:8472",',
      '    "dynamic_variables": {',
      '        "first_name": "Mariana",',
      '        "city": "Sao Paulo",',
      '    },',
      '    "pre_call_script_id": "d2c6a14e-77f9-46df-917c-53bd69c39701",',
      '    "script_ids": ["a1a97f62-9dc1-4ef4-8f2b-dbbd6d86c0b4"],',
      '    "retry_policy": {',
      '        "max_attempts": 3,',
      '        "delay_min_seconds": 30,',
      '        "delay_max_seconds": 120,',
      '    },',
      '    "transfer_target": {',
      '        "type": "user",',
      '        "id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
      '    },',
      '    "safety": {',
      '        "skip_if_called_today": True,',
      '        "skip_if_reached": True,',
      '        "skip_if_converted": True,',
      '    },',
      '}',
      '',
      'response = requests.post(url, headers=headers, json=payload, timeout=30)',
      'response.raise_for_status()',
      'print(response.json())',
    ].join('\n'),
    php: [
      '<?php',
      '',
      '$payload = json_encode([',
      '    "contact" => [',
      '        "identity" => [',
      '            "kind" => "external_id",',
      '            "value" => "crm-contact-84921",',
      '        ],',
      '        "name" => "Mariana Souza",',
      '        "phones" => [',
      '            ["number" => "+5511999990001", "label" => "primary"],',
      '            ["number" => "+5511988880002", "label" => "secondary"],',
      '        ],',
      '    ],',
      '    "ai_agent_id" => "f08c1745-2f11-4fb4-aab1-d120fb7251ce",',
      '    "client_reference" => "operator:8472",',
      '    "dynamic_variables" => [',
      '        "first_name" => "Mariana",',
      '        "city" => "Sao Paulo",',
      '    ],',
      '    "pre_call_script_id" => "d2c6a14e-77f9-46df-917c-53bd69c39701",',
      '    "script_ids" => ["a1a97f62-9dc1-4ef4-8f2b-dbbd6d86c0b4"],',
      '    "retry_policy" => [',
      '        "max_attempts" => 3,',
      '        "delay_min_seconds" => 30,',
      '        "delay_max_seconds" => 120,',
      '    ],',
      '    "transfer_target" => [',
      '        "type" => "user",',
      '        "id" => "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
      '    ],',
      '    "safety" => [',
      '        "skip_if_called_today" => true,',
      '        "skip_if_reached" => true,',
      '        "skip_if_converted" => true,',
      '    ],',
      ']);',
      '',
      `$ch = curl_init("${url}");`,
      'curl_setopt_array($ch, [',
      '    CURLOPT_RETURNTRANSFER => true,',
      '    CURLOPT_POST => true,',
      '    CURLOPT_POSTFIELDS => $payload,',
      '    CURLOPT_HTTPHEADER => [',
      `        "Authorization: Bearer ${DEMO_TOKEN}",`,
      `        "Idempotency-Key: ${DEMO_IDEMPOTENCY_KEY}",`,
      '        "Content-Type: application/json",',
      '    ],',
      ']);',
      '',
      '$body = curl_exec($ch);',
      '$status = curl_getinfo($ch, CURLINFO_RESPONSE_CODE);',
      'curl_close($ch);',
      '',
      'if ($status >= 400) {',
      '    throw new RuntimeException($body);',
      '}',
      '',
      'echo $body;',
    ].join('\n'),
    typescript: [
      `const response = await fetch("${url}", {`,
      '  method: "POST",',
      '  headers: {',
      `    Authorization: "Bearer ${DEMO_TOKEN}",`,
      `    "Idempotency-Key": "${DEMO_IDEMPOTENCY_KEY}",`,
      '    "Content-Type": "application/json",',
      '  },',
      '  body: JSON.stringify({',
      '    contact: {',
      '      identity: {',
      '        kind: "external_id",',
      '        value: "crm-contact-84921",',
      '      },',
      '      name: "Mariana Souza",',
      '      phones: [',
      '        { number: "+5511999990001", label: "primary" },',
      '        { number: "+5511988880002", label: "secondary" },',
      '      ],',
      '    },',
      '    ai_agent_id: "f08c1745-2f11-4fb4-aab1-d120fb7251ce",',
      '    client_reference: "operator:8472",',
      '    dynamic_variables: {',
      '      first_name: "Mariana",',
      '      city: "Sao Paulo",',
      '    },',
      '    pre_call_script_id: "d2c6a14e-77f9-46df-917c-53bd69c39701",',
      '    script_ids: ["a1a97f62-9dc1-4ef4-8f2b-dbbd6d86c0b4"],',
      '    retry_policy: {',
      '      max_attempts: 3,',
      '      delay_min_seconds: 30,',
      '      delay_max_seconds: 120,',
      '    },',
      '    transfer_target: {',
      '      type: "user",',
      '      id: "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
      '    },',
      '    safety: {',
      '      skip_if_called_today: true,',
      '      skip_if_reached: true,',
      '      skip_if_converted: true,',
      '    },',
      '  }),',
      '});',
      '',
      'if (!response.ok) {',
      '  throw new Error(await response.text());',
      '}',
      '',
      'console.log(await response.json());',
    ].join('\n'),
  };
}

export const API_REFERENCE_EXAMPLES: Record<ApiReferenceExampleId, ApiReferenceExampleDefinition> = {
  metaTenant: {
    method: 'GET',
    path: '/public/v1/meta/tenant/',
    fullUrl: `${API_BASE_URL}/meta/tenant/`,
    examples: {
      curl: [
        'curl --request GET \\',
        `  --url "${API_BASE_URL}/meta/tenant/" \\`,
        `  --header "Authorization: Bearer ${DEMO_TOKEN}"`,
      ].join('\n'),
      python: [
        'import requests',
        '',
        `url = "${API_BASE_URL}/meta/tenant/"`,
        'headers = {',
        `    "Authorization": "Bearer ${DEMO_TOKEN}",`,
        '}',
        '',
        'response = requests.get(url, headers=headers, timeout=30)',
        'response.raise_for_status()',
        'print(response.json())',
      ].join('\n'),
      php: [
        '<?php',
        '',
        `$ch = curl_init("${API_BASE_URL}/meta/tenant/");`,
        'curl_setopt_array($ch, [',
        '    CURLOPT_RETURNTRANSFER => true,',
        '    CURLOPT_HTTPHEADER => [',
        `        "Authorization: Bearer ${DEMO_TOKEN}",`,
        '    ],',
        ']);',
        '',
        '$body = curl_exec($ch);',
        '$status = curl_getinfo($ch, CURLINFO_RESPONSE_CODE);',
        '',
        'if ($body === false) {',
        '    throw new RuntimeException(curl_error($ch));',
        '}',
        '',
        'curl_close($ch);',
        '',
        'if ($status >= 400) {',
        '    throw new RuntimeException($body);',
        '}',
        '',
        'echo $body;',
      ].join('\n'),
      typescript: [
        `const response = await fetch("${API_BASE_URL}/meta/tenant/", {`,
        '  method: "GET",',
        '  headers: {',
        `    Authorization: "Bearer ${DEMO_TOKEN}",`,
        '  },',
        '});',
        '',
        'if (!response.ok) {',
        '  throw new Error(await response.text());',
        '}',
        '',
        'console.log(await response.json());',
      ].join('\n'),
    },
    responses: [
      {
        status: 200,
        body: [
          '{',
          '  "id": "d36e5b55-0ec2-4e5b-a53b-2e6b86d339f1",',
          '  "name": "Acme Operacao Brasil",',
          '  "email": "api@acme.example",',
          '  "is_active": true',
          '}',
        ].join('\n'),
      },
      {
        status: 401,
        body: [
          '{',
          '  "detail": "Invalid public API token"',
          '}',
        ].join('\n'),
      },
      {
        status: 429,
        body: [
          '{',
          '  "detail": "Request was throttled. Expected available in 17 seconds."',
          '}',
        ].join('\n'),
      },
    ],
  },
  metaScopes: {
    method: 'GET',
    path: '/public/v1/meta/scopes/',
    fullUrl: `${API_BASE_URL}/meta/scopes/`,
    examples: {
      curl: [
        'curl --request GET \\',
        `  --url "${API_BASE_URL}/meta/scopes/" \\`,
        `  --header "Authorization: Bearer ${DEMO_TOKEN}"`,
      ].join('\n'),
      python: [
        'import requests',
        '',
        `url = "${API_BASE_URL}/meta/scopes/"`,
        'headers = {',
        `    "Authorization": "Bearer ${DEMO_TOKEN}",`,
        '}',
        '',
        'response = requests.get(url, headers=headers, timeout=30)',
        'response.raise_for_status()',
        'print(response.json())',
      ].join('\n'),
      php: [
        '<?php',
        '',
        `$ch = curl_init("${API_BASE_URL}/meta/scopes/");`,
        'curl_setopt_array($ch, [',
        '    CURLOPT_RETURNTRANSFER => true,',
        '    CURLOPT_HTTPHEADER => [',
        `        "Authorization: Bearer ${DEMO_TOKEN}",`,
        '    ],',
        ']);',
        '',
        '$body = curl_exec($ch);',
        '$status = curl_getinfo($ch, CURLINFO_RESPONSE_CODE);',
        '',
        'if ($body === false) {',
        '    throw new RuntimeException(curl_error($ch));',
        '}',
        '',
        'curl_close($ch);',
        '',
        'if ($status >= 400) {',
        '    throw new RuntimeException($body);',
        '}',
        '',
        'echo $body;',
      ].join('\n'),
      typescript: [
        `const response = await fetch("${API_BASE_URL}/meta/scopes/", {`,
        '  method: "GET",',
        '  headers: {',
        `    Authorization: "Bearer ${DEMO_TOKEN}",`,
        '  },',
        '});',
        '',
        'if (!response.ok) {',
        '  throw new Error(await response.text());',
        '}',
        '',
        'console.log(await response.json());',
      ].join('\n'),
    },
    responses: [
      {
        status: 200,
        body: [
          '{',
          '  "credential": {',
          '    "id": "bf7112e1-2a5f-4ba5-b9f5-6cb5f98dca02",',
          '    "name": "ERP integration",',
          '    "key_id": "ctt_live_01abc123def45678",',
          '    "secret_last4": "4f9c",',
          '    "scopes": [',
          '      "campaigns.read",',
          '      "calls.read",',
          '      "calls.write",',
          '      "calls.authorize_requested",',
          '      "agents.read",',
          '      "scripts.read",',
          '      "users.read",',
          '      "transfer_groups.read"',
          '    ],',
          '    "allowed_ips": [',
          '      "203.0.113.10"',
          '    ],',
          '    "rate_limit_per_minute": 120,',
          '    "is_active": true,',
          '    "expires_at": null,',
          '    "last_used_at": "2026-04-23T18:42:10Z",',
          '    "last_used_ip": "203.0.113.10",',
          '    "created_by_name": "Platform Admin",',
          '    "revoked_by_name": null,',
          '    "revoked_at": null,',
          '    "revoke_reason": "",',
          '    "created_at": "2026-04-23T15:09:22Z",',
          '    "updated_at": "2026-04-23T18:42:10Z"',
          '  },',
          '  "available_scopes": [',
          '    {',
          '      "code": "campaigns.read",',
          '      "label": "Read campaigns"',
          '    },',
          '    {',
          '      "code": "calls.read",',
          '      "label": "Read calls"',
          '    },',
          '    {',
          '      "code": "calls.write",',
          '      "label": "Create AI-agent calls"',
          '    },',
          '    {',
          '      "code": "calls.authorize_requested",',
          '      "label": "Authorize user-requested calls"',
          '    },',
          '    {',
          '      "code": "agents.read",',
          '      "label": "Read AI agents"',
          '    },',
          '    {',
          '      "code": "scripts.read",',
          '      "label": "Read scripts"',
          '    },',
          '    {',
          '      "code": "users.read",',
          '      "label": "Read users"',
          '    },',
          '    {',
          '      "code": "transfer_groups.read",',
          '      "label": "Read transfer groups"',
          '    }',
          '  ]',
          '}',
        ].join('\n'),
      },
      {
        status: 401,
        body: [
          '{',
          '  "detail": "Public API credential expired"',
          '}',
        ].join('\n'),
      },
      {
        status: 429,
        body: [
          '{',
          '  "detail": "Request was throttled. Expected available in 12 seconds."',
          '}',
        ].join('\n'),
      },
    ],
  },
  campaignsList: {
    method: 'GET',
    path: '/public/v1/campaigns/',
    fullUrl: `${API_BASE_URL}/campaigns/?page=1&page_size=2&ordering=name&status=ready,active`,
    examples: {
      curl: [
        'curl --request GET \\',
        `  --url "${API_BASE_URL}/campaigns/?page=1&page_size=2&ordering=name&status=ready,active" \\`,
        `  --header "Authorization: Bearer ${DEMO_TOKEN}"`,
      ].join('\n'),
      python: [
        'import requests',
        '',
        `url = "${API_BASE_URL}/campaigns/"`,
        'headers = {',
        `    "Authorization": "Bearer ${DEMO_TOKEN}",`,
        '}',
        'params = {',
        '    "page": 1,',
        '    "page_size": 2,',
        '    "ordering": "name",',
        '    "status": "ready,active",',
        '}',
        '',
        'response = requests.get(url, headers=headers, params=params, timeout=30)',
        'response.raise_for_status()',
        'print(response.json())',
      ].join('\n'),
      php: [
        '<?php',
        '',
        '$query = http_build_query([',
        '    "page" => 1,',
        '    "page_size" => 2,',
        '    "ordering" => "name",',
        '    "status" => "ready,active",',
        ']);',
        `$ch = curl_init("${API_BASE_URL}/campaigns/?" . $query);`,
        'curl_setopt_array($ch, [',
        '    CURLOPT_RETURNTRANSFER => true,',
        '    CURLOPT_HTTPHEADER => [',
        `        "Authorization: Bearer ${DEMO_TOKEN}",`,
        '    ],',
        ']);',
        '',
        '$body = curl_exec($ch);',
        '$status = curl_getinfo($ch, CURLINFO_RESPONSE_CODE);',
        '',
        'if ($body === false) {',
        '    throw new RuntimeException(curl_error($ch));',
        '}',
        '',
        'curl_close($ch);',
        '',
        'if ($status >= 400) {',
        '    throw new RuntimeException($body);',
        '}',
        '',
        'echo $body;',
      ].join('\n'),
      typescript: [
        `const url = new URL("${API_BASE_URL}/campaigns/");`,
        'url.searchParams.set("page", "1");',
        'url.searchParams.set("page_size", "2");',
        'url.searchParams.set("ordering", "name");',
        'url.searchParams.set("status", "ready,active");',
        '',
        'const response = await fetch(url, {',
        '  method: "GET",',
        '  headers: {',
        `    Authorization: "Bearer ${DEMO_TOKEN}",`,
        '  },',
        '});',
        '',
        'if (!response.ok) {',
        '  throw new Error(await response.text());',
        '}',
        '',
        'console.log(await response.json());',
      ].join('\n'),
    },
    responses: [
      {
        status: 200,
        body: [
          '{',
          '  "count": 3,',
          `  "next": "${API_BASE_URL}/campaigns/?page=2&page_size=2&ordering=name&status=ready,active",`,
          '  "previous": null,',
          '  "results": [',
          '    {',
          '      "id": "9d3bbcb1-fba0-4f4b-9af4-8ab6de078f11",',
          `      "canonical_url": "${APP_BASE_URL}/campaigns/9d3bbcb1-fba0-4f4b-9af4-8ab6de078f11",`,
          '      "created_by_id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
          '      "created_by_name": "Mariana Operacoes",',
          '      "name": "Campanha Alpha",',
          '      "description": "Primeira campanha publica",',
          '      "status": "ready",',
          '      "status_reason": null,',
          '      "campaign_type": "ai",',
          '      "ai_agent_provider_id": "1d925d88-fcda-4d17-8fa3-34a37c0d7d11",',
          '      "ai_agent_provider_name": "Provider Principal",',
          '      "ai_agent_provider_type": "cloud",',
          '      "ai_agent_id": "f08c1745-2f11-4fb4-aab1-d120fb7251ce",',
          '      "ai_agent_name": "Bia Comercial",',
          '      "sip_trunk_id": null,',
          '      "sip_trunk_name": null,',
          '      "max_concurrent_calls": 2,',
          '      "tags": ["vip"],',
          '      "transfer_target_type": "user",',
          '      "transfer_target_id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
          '      "transfer_target_name": "Mariana Operacoes",',
          '      "transfer_target_extension": "4010",',
          '      "import_status": "completed",',
          '      "total_contacts": 10,',
          '      "total_phones": 12,',
          '      "pending_contacts": 4,',
          '      "in_progress_contacts": 1,',
          '      "reached_contacts": 6,',
          '      "converted_contacts": 2,',
          '      "optout_contacts": 0,',
          '      "mismatch_contacts": 0,',
          '      "exhausted_contacts": 0,',
          '      "no_valid_phones_contacts": 1,',
          '      "skipped_contacts": 0,',
          '      "total_calls": 12,',
          '      "connected_calls": 5,',
          '      "conversion_eligible_calls": 4,',
          '      "failed_calls": 3,',
          '      "converted_calls": 2,',
          '      "ai_billed_seconds": 121,',
          '      "ai_gross_billed_seconds": 126,',
          '      "ai_refunded_seconds": 5,',
          '      "telephony_billed_seconds": 30,',
          '      "telephony_gross_billed_seconds": 30,',
          '      "telephony_refunded_seconds": 0,',
          '      "conversion_rate": 50.0,',
          '      "progress_percentage": 60.0,',
          '      "created_at": "2026-04-23T14:20:11Z",',
          '      "updated_at": "2026-04-23T14:40:55Z",',
          '      "started_at": null,',
          '      "completed_at": null',
          '    }',
          '  ]',
          '}',
        ].join('\n'),
      },
      {
        status: 401,
        body: ['{', '  "detail": "Invalid public API token"', '}'].join('\n'),
      },
      {
        status: 403,
        body: ['{', '  "detail": "You do not have permission to perform this action."', '}'].join('\n'),
      },
      {
        status: 429,
        body: ['{', '  "detail": "Request was throttled. Expected available in 11 seconds."', '}'].join('\n'),
      },
    ],
  },
  campaignDetail: {
    method: 'GET',
    path: '/public/v1/campaigns/{id}/',
    fullUrl: `${API_BASE_URL}/campaigns/9d3bbcb1-fba0-4f4b-9af4-8ab6de078f11/`,
    examples: {
      curl: [
        'curl --request GET \\',
        `  --url "${API_BASE_URL}/campaigns/9d3bbcb1-fba0-4f4b-9af4-8ab6de078f11/" \\`,
        `  --header "Authorization: Bearer ${DEMO_TOKEN}"`,
      ].join('\n'),
      python: [
        'import requests',
        '',
        `url = "${API_BASE_URL}/campaigns/9d3bbcb1-fba0-4f4b-9af4-8ab6de078f11/"`,
        'headers = {',
        `    "Authorization": "Bearer ${DEMO_TOKEN}",`,
        '}',
        '',
        'response = requests.get(url, headers=headers, timeout=30)',
        'response.raise_for_status()',
        'print(response.json())',
      ].join('\n'),
      php: [
        '<?php',
        '',
        `$ch = curl_init("${API_BASE_URL}/campaigns/9d3bbcb1-fba0-4f4b-9af4-8ab6de078f11/");`,
        'curl_setopt_array($ch, [',
        '    CURLOPT_RETURNTRANSFER => true,',
        '    CURLOPT_HTTPHEADER => [',
        `        "Authorization: Bearer ${DEMO_TOKEN}",`,
        '    ],',
        ']);',
        '',
        '$body = curl_exec($ch);',
        '$status = curl_getinfo($ch, CURLINFO_RESPONSE_CODE);',
        '',
        'if ($body === false) {',
        '    throw new RuntimeException(curl_error($ch));',
        '}',
        '',
        'curl_close($ch);',
        '',
        'if ($status >= 400) {',
        '    throw new RuntimeException($body);',
        '}',
        '',
        'echo $body;',
      ].join('\n'),
      typescript: [
        `const response = await fetch("${API_BASE_URL}/campaigns/9d3bbcb1-fba0-4f4b-9af4-8ab6de078f11/", {`,
        '  method: "GET",',
        '  headers: {',
        `    Authorization: "Bearer ${DEMO_TOKEN}",`,
        '  },',
        '});',
        '',
        'if (!response.ok) {',
        '  throw new Error(await response.text());',
        '}',
        '',
        'console.log(await response.json());',
      ].join('\n'),
    },
    responses: [
      {
        status: 200,
        body: [
          '{',
          '  "id": "9d3bbcb1-fba0-4f4b-9af4-8ab6de078f11",',
          `  "canonical_url": "${APP_BASE_URL}/campaigns/9d3bbcb1-fba0-4f4b-9af4-8ab6de078f11",`,
          '  "created_by_id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
          '  "created_by_name": "Mariana Operacoes",',
          '  "name": "Campanha Alpha",',
          '  "description": "Primeira campanha publica",',
          '  "status": "active",',
          '  "status_reason": null,',
          '  "campaign_type": "ai",',
          '  "ai_agent_provider_id": "1d925d88-fcda-4d17-8fa3-34a37c0d7d11",',
          '  "ai_agent_provider_name": "Provider Principal",',
          '  "ai_agent_provider_type": "cloud",',
          '  "ai_agent_id": "f08c1745-2f11-4fb4-aab1-d120fb7251ce",',
          '  "ai_agent_name": "Bia Comercial",',
          '  "sip_trunk_id": null,',
          '  "sip_trunk_name": null,',
          '  "max_concurrent_calls": 2,',
          '  "max_retries": 5,',
          '  "retry_delay_min_seconds": 30,',
          '  "retry_delay_max_seconds": 90,',
          '  "schedule": [],',
          '  "tags": ["vip"],',
          '  "transfer_target_type": "user",',
          '  "transfer_target_id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
          '  "transfer_target_name": "Mariana Operacoes",',
          '  "transfer_target_extension": "4010",',
          '  "ivr_dtmf_map": {},',
          '  "import_status": "completed",',
          '  "import_progress": 100.0,',
          '  "import_processed_rows": 10,',
          '  "import_total_rows": 10,',
          '  "import_started_at": "2026-04-23T13:55:10Z",',
          '  "import_completed_at": "2026-04-23T13:56:42Z",',
          '  "total_contacts": 10,',
          '  "total_phones": 12,',
          '  "pending_contacts": 4,',
          '  "in_progress_contacts": 1,',
          '  "reached_contacts": 6,',
          '  "converted_contacts": 2,',
          '  "optout_contacts": 0,',
          '  "mismatch_contacts": 0,',
          '  "exhausted_contacts": 0,',
          '  "no_valid_phones_contacts": 1,',
          '  "skipped_contacts": 0,',
          '  "total_calls": 12,',
          '  "connected_calls": 5,',
          '  "conversion_eligible_calls": 4,',
          '  "failed_calls": 3,',
          '  "converted_calls": 2,',
          '  "ai_billed_seconds": 121,',
          '  "ai_gross_billed_seconds": 126,',
          '  "ai_refunded_seconds": 5,',
          '  "telephony_billed_seconds": 30,',
          '  "telephony_gross_billed_seconds": 30,',
          '  "telephony_refunded_seconds": 0,',
          '  "conversion_rate": 50.0,',
          '  "progress_percentage": 60.0,',
          '  "created_at": "2026-04-23T13:54:22Z",',
          '  "updated_at": "2026-04-23T14:40:55Z",',
          '  "started_at": "2026-04-23T14:05:00Z",',
          '  "completed_at": null',
          '}',
        ].join('\n'),
      },
      {
        status: 401,
        body: ['{', '  "detail": "Invalid public API token"', '}'].join('\n'),
      },
      {
        status: 403,
        body: ['{', '  "detail": "You do not have permission to perform this action."', '}'].join('\n'),
      },
    ],
  },
  callsList: {
    method: 'GET',
    path: '/public/v1/calls/',
    fullUrl: `${API_BASE_URL}/calls/?page=1&page_size=2&ordering=-created_at&public_api_call_contact_id=777eb68e-7f15-4538-a07b-898595d3262c`,
    examples: {
      curl: [
        'curl --request GET \\',
        `  --url "${API_BASE_URL}/calls/?page=1&page_size=2&ordering=-created_at&public_api_call_contact_id=777eb68e-7f15-4538-a07b-898595d3262c" \\`,
        `  --header "Authorization: Bearer ${DEMO_TOKEN}"`,
      ].join('\n'),
      python: [
        'import requests',
        '',
        `url = "${API_BASE_URL}/calls/"`,
        'headers = {',
        `    "Authorization": "Bearer ${DEMO_TOKEN}",`,
        '}',
        'params = {',
        '    "page": 1,',
        '    "page_size": 2,',
        '    "ordering": "-created_at",',
        '    "public_api_call_contact_id": "777eb68e-7f15-4538-a07b-898595d3262c",',
        '}',
        '',
        'response = requests.get(url, headers=headers, params=params, timeout=30)',
        'response.raise_for_status()',
        'print(response.json())',
      ].join('\n'),
      php: [
        '<?php',
        '',
        '$query = http_build_query([',
        '    "page" => 1,',
        '    "page_size" => 2,',
        '    "ordering" => "-created_at",',
        '    "public_api_call_contact_id" => "777eb68e-7f15-4538-a07b-898595d3262c",',
        ']);',
        `$ch = curl_init("${API_BASE_URL}/calls/?" . $query);`,
        'curl_setopt_array($ch, [',
        '    CURLOPT_RETURNTRANSFER => true,',
        '    CURLOPT_HTTPHEADER => [',
        `        "Authorization: Bearer ${DEMO_TOKEN}",`,
        '    ],',
        ']);',
        '',
        '$body = curl_exec($ch);',
        '$status = curl_getinfo($ch, CURLINFO_RESPONSE_CODE);',
        '',
        'if ($body === false) {',
        '    throw new RuntimeException(curl_error($ch));',
        '}',
        '',
        'curl_close($ch);',
        '',
        'if ($status >= 400) {',
        '    throw new RuntimeException($body);',
        '}',
        '',
        'echo $body;',
      ].join('\n'),
      typescript: [
        `const url = new URL("${API_BASE_URL}/calls/");`,
        'url.searchParams.set("page", "1");',
        'url.searchParams.set("page_size", "2");',
        'url.searchParams.set("ordering", "-created_at");',
        'url.searchParams.set("public_api_call_contact_id", "777eb68e-7f15-4538-a07b-898595d3262c");',
        '',
        'const response = await fetch(url, {',
        '  method: "GET",',
        '  headers: {',
        `    Authorization: "Bearer ${DEMO_TOKEN}",`,
        '  },',
        '});',
        '',
        'if (!response.ok) {',
        '  throw new Error(await response.text());',
        '}',
        '',
        'console.log(await response.json());',
      ].join('\n'),
    },
    responses: [
      {
        status: 200,
        body: [
          '{',
          '  "count": 1,',
          '  "next": null,',
          '  "previous": null,',
          '  "results": [',
          '    {',
          '      "id": "2ced4103-faa2-44ec-8b8e-7a86bbd2d410",',
          `      "canonical_url": "${APP_BASE_URL}/calls?call=2ced4103-faa2-44ec-8b8e-7a86bbd2d410",`,
          '      "direction": "outbound",',
          '      "origin": "public_api",',
          '      "call_type": "ai_agent",',
          '      "status": "completed",',
          '      "to_number": "5511999990001",',
          '      "from_number": "551133330001",',
          '      "duration": 120,',
          '      "ai_billed_seconds": 121,',
          '      "ai_gross_billed_seconds": 126,',
          '      "ai_refunded_seconds": 5,',
          '      "telephony_billed_seconds": 30,',
          '      "telephony_gross_billed_seconds": 30,',
          '      "telephony_refunded_seconds": 0,',
          '      "is_converted": true,',
          '      "is_optout": false,',
          '      "is_mismatch": false,',
          '      "initiated_at": "2026-04-23T14:10:00Z",',
          '      "answered_at": "2026-04-23T14:10:05Z",',
          '      "ended_at": "2026-04-23T14:12:11Z",',
          '      "created_at": "2026-04-23T14:09:58Z",',
          '      "updated_at": "2026-04-23T14:12:12Z",',
          '      "campaign_id": null,',
          '      "campaign_name": null,',
          '      "campaign_contact_id": null,',
          '      "campaign_contact_display_name": null,',
          '      "public_api_call_contact_id": "777eb68e-7f15-4538-a07b-898595d3262c",',
          '      "client_reference": "operator:8472",',
          '      "call_authorization": {},',
          '      "retry_policy": {',
          '        "max_attempts": 3,',
          '        "delay_min_seconds": 30,',
          '        "delay_max_seconds": 120',
          '      },',
          '      "attempts": {',
          '        "count": 2,',
          '        "remaining": 1,',
          '        "next_attempt_at": null',
          '      },',
          '      "public_api_call_contact_status": "converted",',
          '      "public_api_call_contact_phone_id": "b8a5504f-ff3b-45dd-8954-3d908fe54bbf",',
          '      "public_api_call_contact_phone_status": "converted",',
          '      "public_api_call_contact_phone_position": 0,',
          '      "agent_id": "f08c1745-2f11-4fb4-aab1-d120fb7251ce",',
          '      "agent_name": "Bia Comercial",',
          '      "created_by_id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
          '      "created_by_name": "Mariana Operacoes",',
          '      "created_by_extension": "4010",',
          '      "telephony_transfer_started_at": "2026-04-23T14:11:00Z",',
          '      "telephony_transfer_wait_started_at": "2026-04-23T14:11:05Z",',
          '      "telephony_transfer_connected_at": "2026-04-23T14:11:18Z",',
          '      "telephony_transfer_wait_ms": 13000,',
          '      "transfer_destination_type": "internal_extension",',
          '      "transfer_destination_value": "4010",',
          '      "transfer_target_user_id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
          '      "transfer_target_user_name": "Mariana Operacoes",',
          '      "transfer_target_user_extension": "4010",',
          '      "transfer_target_group_id": null,',
          '      "transfer_target_group_name": null,',
          '      "interaction_claimed_by_id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
          '      "interaction_claimed_by_name": "Mariana Operacoes",',
          '      "interaction_claimed_at": "2026-04-23T14:11:05Z",',
          '      "interaction_answered_by_id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
          '      "interaction_answered_by_name": "Mariana Operacoes",',
          '      "interaction_answered_at": "2026-04-23T14:11:18Z",',
          '      "interaction_answered_extension": "4010"',
          '    }',
          '  ]',
          '}',
        ].join('\n'),
      },
      {
        status: 401,
        body: ['{', '  "detail": "Invalid public API token"', '}'].join('\n'),
      },
      {
        status: 403,
        body: ['{', '  "detail": "You do not have permission to perform this action."', '}'].join('\n'),
      },
      {
        status: 429,
        body: ['{', '  "detail": "Request was throttled. Expected available in 8 seconds."', '}'].join('\n'),
      },
    ],
  },
  callDetail: {
    method: 'GET',
    path: '/public/v1/calls/{id}/',
    fullUrl: `${API_BASE_URL}/calls/2ced4103-faa2-44ec-8b8e-7a86bbd2d410/`,
    examples: {
      curl: [
        'curl --request GET \\',
        `  --url "${API_BASE_URL}/calls/2ced4103-faa2-44ec-8b8e-7a86bbd2d410/" \\`,
        `  --header "Authorization: Bearer ${DEMO_TOKEN}"`,
      ].join('\n'),
      python: [
        'import requests',
        '',
        `url = "${API_BASE_URL}/calls/2ced4103-faa2-44ec-8b8e-7a86bbd2d410/"`,
        'headers = {',
        `    "Authorization": "Bearer ${DEMO_TOKEN}",`,
        '}',
        '',
        'response = requests.get(url, headers=headers, timeout=30)',
        'response.raise_for_status()',
        'print(response.json())',
      ].join('\n'),
      php: [
        '<?php',
        '',
        `$ch = curl_init("${API_BASE_URL}/calls/2ced4103-faa2-44ec-8b8e-7a86bbd2d410/");`,
        'curl_setopt_array($ch, [',
        '    CURLOPT_RETURNTRANSFER => true,',
        '    CURLOPT_HTTPHEADER => [',
        `        "Authorization: Bearer ${DEMO_TOKEN}",`,
        '    ],',
        ']);',
        '',
        '$body = curl_exec($ch);',
        '$status = curl_getinfo($ch, CURLINFO_RESPONSE_CODE);',
        '',
        'if ($body === false) {',
        '    throw new RuntimeException(curl_error($ch));',
        '}',
        '',
        'curl_close($ch);',
        '',
        'if ($status >= 400) {',
        '    throw new RuntimeException($body);',
        '}',
        '',
        'echo $body;',
      ].join('\n'),
      typescript: [
        `const response = await fetch("${API_BASE_URL}/calls/2ced4103-faa2-44ec-8b8e-7a86bbd2d410/", {`,
        '  method: "GET",',
        '  headers: {',
        `    Authorization: "Bearer ${DEMO_TOKEN}",`,
        '  },',
        '});',
        '',
        'if (!response.ok) {',
        '  throw new Error(await response.text());',
        '}',
        '',
        'console.log(await response.json());',
      ].join('\n'),
    },
    responses: [
      {
        status: 200,
        body: [
          '{',
          '  "id": "2ced4103-faa2-44ec-8b8e-7a86bbd2d410",',
          `  "canonical_url": "${APP_BASE_URL}/calls?call=2ced4103-faa2-44ec-8b8e-7a86bbd2d410",`,
          '  "direction": "outbound",',
          '  "origin": "public_api",',
          '  "call_type": "ai_agent",',
          '  "status": "completed",',
          '  "to_number": "5511999990001",',
          '  "from_number": "551133330001",',
          '  "from_number_real": "551133339999",',
          '  "duration": 120,',
          '  "ai_billed_seconds": 121,',
          '  "ai_gross_billed_seconds": 126,',
          '  "ai_refunded_seconds": 5,',
          '  "telephony_billed_seconds": 30,',
          '  "telephony_gross_billed_seconds": 30,',
          '  "telephony_refunded_seconds": 0,',
          '  "ring_duration": 4,',
          '  "is_converted": true,',
          '  "is_optout": false,',
          '  "is_mismatch": false,',
          '  "initiated_at": "2026-04-23T14:10:00Z",',
          '  "answered_at": "2026-04-23T14:10:05Z",',
          '  "ended_at": "2026-04-23T14:12:11Z",',
          '  "created_at": "2026-04-23T14:09:58Z",',
          '  "updated_at": "2026-04-23T14:12:12Z",',
          '  "campaign_id": null,',
          '  "campaign_name": null,',
          '  "campaign_contact_id": null,',
          '  "campaign_contact_display_name": null,',
          '  "public_api_call_contact_id": "777eb68e-7f15-4538-a07b-898595d3262c",',
          '  "client_reference": "operator:8472",',
          '  "call_authorization": {},',
          '  "retry_policy": {',
          '    "max_attempts": 3,',
          '    "delay_min_seconds": 30,',
          '    "delay_max_seconds": 120',
          '  },',
          '  "attempts": {',
          '    "count": 2,',
          '    "remaining": 1,',
          '    "next_attempt_at": null',
          '  },',
          '  "public_api_call_contact_status": "converted",',
          '  "public_api_call_contact_phone_id": "b8a5504f-ff3b-45dd-8954-3d908fe54bbf",',
          '  "public_api_call_contact_phone_status": "converted",',
          '  "public_api_call_contact_phone_position": 0,',
          '  "agent_id": "f08c1745-2f11-4fb4-aab1-d120fb7251ce",',
          '  "agent_name": "Bia Comercial",',
          '  "created_by_id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
          '  "created_by_name": "Mariana Operacoes",',
          '  "created_by_extension": "4010",',
          '  "to_number_country_code": "55",',
          '  "to_number_area_code": "11",',
          '  "to_number_phone_type": "mobile",',
          '  "to_number_state_code": "SP",',
          '  "to_number_state_name": "Sao Paulo",',
          '  "to_number_region_code": "southeast",',
          '  "to_number_region_name": "Southeast",',
          '  "to_number_timezone": "America/Sao_Paulo",',
          '  "sip_trunk_id": null,',
          '  "sip_trunk_name": null,',
          '  "phone_label": "principal",',
          '  "recording_download_url": null,',
          '  "variables": {',
          '    "nome": "Mariana",',
          '    "plano": "premium"',
          '  },',
          '  "data_collected": {',
          '    "canal_preferido": {',
          '      "value": "whatsapp",',
          '      "rationale": "Cliente pediu continuidade pelo WhatsApp.",',
          '      "__source": {',
          '        "scope": "segment",',
          '        "segment_type": "ai",',
          '        "segment_sequence": 1,',
          '        "segment_id": "c9a44987-1439-4a1f-a0a1-a2bec5af4e96"',
          '      }',
          '    }',
          '  },',
          '  "scheduled_at": "2026-04-23T14:09:00Z",',
          '  "queued_at": "2026-04-23T14:09:59Z",',
          '  "ringing_at": "2026-04-23T14:10:01Z",',
          '  "telephony_transfer_started_at": "2026-04-23T14:11:00Z",',
          '  "telephony_transfer_wait_started_at": "2026-04-23T14:11:05Z",',
          '  "telephony_transfer_connected_at": "2026-04-23T14:11:18Z",',
          '  "telephony_transfer_wait_ms": 13000,',
          '  "transfer_destination_type": "internal_extension",',
          '  "transfer_destination_value": "4010",',
          '  "transfer_target_user_id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
          '  "transfer_target_user_name": "Mariana Operacoes",',
          '  "transfer_target_user_extension": "4010",',
          '  "transfer_target_group_id": null,',
          '  "transfer_target_group_name": null,',
          '  "interaction_claimed_by_id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
          '  "interaction_claimed_by_name": "Mariana Operacoes",',
          '  "interaction_claimed_at": "2026-04-23T14:11:05Z",',
          '  "interaction_answered_by_id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
          '  "interaction_answered_by_name": "Mariana Operacoes",',
          '  "interaction_answered_at": "2026-04-23T14:11:18Z",',
          '  "interaction_answered_extension": "4010",',
          '  "origin_line_name": null,',
          '  "ivr_selected_digit": null,',
          '  "ivr_selected_status": null,',
          '  "ivr_resolution": null,',
          '  "ivr_audio_heard_seconds": null,',
          '  "ivr_audio_heard_to_end": null,',
          '  "termination_origin": "system",',
          '  "segments": [',
          '    {',
          '      "id": "c9a44987-1439-4a1f-a0a1-a2bec5af4e96",',
          '      "segment_type": "ai",',
          '      "sequence": 1,',
          '      "started_at": "2026-04-23T14:10:05Z",',
          '      "connected_at": "2026-04-23T14:10:06Z",',
          '      "ended_at": "2026-04-23T14:12:11Z",',
          '      "duration_seconds": 126,',
          '      "billed_seconds": 126,',
          '      "gross_billed_seconds": 126,',
          '      "refunded_seconds": 0,',
          '      "recording_download_url": null,',
          '      "transcript": "Agente: Oi, Mariana. Cliente: Pode mandar no WhatsApp.",',
          '      "transcript_full": [',
          '        {',
          '          "role": "agent",',
          '          "message": "Oi, Mariana.",',
          '          "original_message": "Oi, Mariana.",',
          '          "time_in_call_secs": 1.2',
          '        },',
          '        {',
          '          "role": "user",',
          '          "message": "Pode mandar no WhatsApp.",',
          '          "original_message": null,',
          '          "time_in_call_secs": 4.8',
          '        }',
          '      ],',
          '      "conversation_summary": "Cliente aceitou continuidade por WhatsApp.",',
          '      "ivr_dtmf_digit": null,',
          '      "ivr_dtmf_received_at": null,',
          '      "ivr_dtmf_mapped_status": null,',
          '      "ivr_end_reason": null,',
          '      "voicemail_detection": null',
          '    }',
          '  ]',
          '}',
        ].join('\n'),
      },
      {
        status: 401,
        body: ['{', '  "detail": "Invalid public API token"', '}'].join('\n'),
      },
      {
        status: 403,
        body: ['{', '  "detail": "You do not have permission to perform this action."', '}'].join('\n'),
      },
    ],
  },
  callsCreate: {
    method: 'POST',
    path: '/public/v1/calls/',
    fullUrl: `${API_BASE_URL}/calls/`,
    examples: buildCreateCallExamples(`${API_BASE_URL}/calls/`),
    responses: [
      {
        status: 201,
        body: [
          '{',
          '  "id": "2ced4103-faa2-44ec-8b8e-7a86bbd2d410",',
          '  "resource_type": "public_api_call_contact",',
          '  "status": "in_progress",',
          '  "client_reference": "operator:8472",',
          '  "call_authorization": {},',
          '  "contact": {',
          '    "id": "6bcdb463-0cb3-47d4-8878-331c0320c013",',
          '    "identity": {',
          '      "kind": "external_id",',
          '      "value": "crm-contact-84921"',
          '    }',
          '  },',
          '  "current_call": {',
          '    "id": "82d8bd8b-e820-404d-bc5f-6983a4e4d571",',
          '    "to_number": "+5511999990001",',
          '    "status": "queued"',
          '  },',
          '  "phones": [',
          '    {',
          '      "id": "931d01d2-2dfa-4f75-8d22-cf6fe2ad0480",',
          '      "number": "+5511999990001",',
          '      "position": 0,',
          '      "status": "attempting",',
          '      "status_reason": null,',
          '      "call_id": "82d8bd8b-e820-404d-bc5f-6983a4e4d571",',
          '      "last_call_status": "queued"',
          '    },',
          '    {',
          '      "id": "a31a3ec8-2ad4-4635-85f8-7f952f1842b1",',
          '      "number": "+5511988880002",',
          '      "position": 1,',
          '      "status": "pending",',
          '      "status_reason": null,',
          '      "call_id": null,',
          '      "last_call_status": null',
          '    }',
          '  ],',
          '  "ai_agent_id": "f08c1745-2f11-4fb4-aab1-d120fb7251ce",',
          '  "pre_call_script_id": "d2c6a14e-77f9-46df-917c-53bd69c39701",',
          '  "pre_call": {',
          '    "attempt_count": 1,',
          '    "reason_code": "customer_eligible",',
          '    "next_retry_at": null,',
          '    "execution_id": "4dc43f20-c712-4538-9a61-670483ac5c52"',
          '  },',
          '  "retry_policy": {',
          '    "max_attempts": 3,',
          '    "delay_min_seconds": 30,',
          '    "delay_max_seconds": 120',
          '  },',
          '  "attempts": {',
          '    "count": 1,',
          '    "remaining": 2,',
          '    "next_attempt_at": null',
          '  },',
          '  "script_ids": ["a1a97f62-9dc1-4ef4-8f2b-dbbd6d86c0b4"],',
          '  "transfer_target": {',
          '    "type": "user",',
          '    "id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a"',
          '  },',
          '  "dynamic_variables": {',
          '    "first_name": "Mariana",',
          '    "city": "Sao Paulo"',
          '  },',
          '  "dynamic_variable_keys": ["city", "first_name"],',
          '  "idempotency_key_replayed": false,',
          '  "safety": {',
          '    "skip_if_called_today": true,',
          '    "skip_if_reached": true,',
          '    "skip_if_converted": true',
          '  },',
          '  "capacity": {',
          '    "state": null,',
          '    "wait_expires_at": null',
          '  },',
          '  "operating_hours": {',
          '    "state": null,',
          '    "wait_expires_at": null',
          '  },',
          '  "created_at": "2026-05-27T18:22:10Z"',
          '}',
        ].join('\n'),
      },
      {
        status: 201,
        body: [
          '{',
          '  "id": "777eb68e-7f15-4538-a07b-898595d3262c",',
          '  "resource_type": "public_api_call_contact",',
          '  "status": "waiting_capacity",',
          '  "client_reference": "operator:8472",',
          '  "contact": {',
          '    "id": "6bcdb463-0cb3-47d4-8878-331c0320c013",',
          '    "identity": {',
          '      "kind": "external_id",',
          '      "value": "crm-contact-84921"',
          '    }',
          '  },',
          '  "current_call": {',
          '    "id": "4f586584-63d7-452d-b41a-434db9ec2ed5",',
          '    "to_number": "+5511999990001",',
          '    "status": "waiting_capacity"',
          '  },',
          '  "phones": [',
          '    {',
          '      "id": "b8a5504f-ff3b-45dd-8954-3d908fe54bbf",',
          '      "number": "+5511999990001",',
          '      "position": 0,',
          '      "status": "attempting",',
          '      "status_reason": null,',
          '      "call_id": "4f586584-63d7-452d-b41a-434db9ec2ed5",',
          '      "last_call_status": "waiting_capacity"',
          '    }',
          '  ],',
          '  "ai_agent_id": "f08c1745-2f11-4fb4-aab1-d120fb7251ce",',
          '  "pre_call_script_id": null,',
          '  "pre_call": {',
          '    "attempt_count": 0,',
          '    "reason_code": null,',
          '    "next_retry_at": null,',
          '    "execution_id": null',
          '  },',
          '  "retry_policy": {',
          '    "max_attempts": 3,',
          '    "delay_min_seconds": 30,',
          '    "delay_max_seconds": 120',
          '  },',
          '  "attempts": {',
          '    "count": 0,',
          '    "remaining": 3,',
          '    "next_attempt_at": null',
          '  },',
          '  "script_ids": [],',
          '  "transfer_target": null,',
          '  "dynamic_variables": {',
          '    "first_name": "Mariana",',
          '    "city": "Sao Paulo"',
          '  },',
          '  "dynamic_variable_keys": ["city", "first_name"],',
          '  "idempotency_key_replayed": false,',
          '  "safety": {',
          '    "skip_if_called_today": true,',
          '    "skip_if_reached": true,',
          '    "skip_if_converted": true',
          '  },',
          '  "capacity": {',
          '    "state": "waiting",',
          '    "wait_expires_at": "2026-05-27T18:37:10Z"',
          '  },',
          '  "operating_hours": {',
          '    "state": "within_operating_hours_or_not_applicable",',
          '    "wait_expires_at": null',
          '  },',
          '  "created_at": "2026-05-27T18:22:10Z"',
          '}',
        ].join('\n'),
      },
      {
        status: 400,
        body: [
          '{',
          '  "dynamic_variables": {',
          '    "code": "missing_dynamic_variables",',
          '    "missing": ["city"],',
          '    "message": "Missing dynamic variables required by this agent: city."',
          '  }',
          '}',
        ].join('\n'),
      },
      {
        status: 409,
        body: [
          '{',
          '  "error": "idempotency_payload_mismatch",',
          '  "message": "Idempotency-Key was already used with a different payload."',
          '}',
        ].join('\n'),
      },
      {
        status: 409,
        body: [
          '{',
          '  "error": "call_safety_blocked",',
          '  "reason": "already_called_today",',
          '  "message": "A call to this phone was already created today.",',
          '  "safety": {',
          '    "skip_if_called_today": true,',
          '    "skip_if_reached": true,',
          '    "skip_if_converted": true',
          '  }',
          '}',
        ].join('\n'),
      },
      {
        status: 429,
        body: ['{', '  "detail": "Request was throttled. Expected available in 8 seconds."', '}'].join('\n'),
      },
    ],
  },
  agentsList: {
    method: 'GET',
    path: '/public/v1/agents/',
    fullUrl: `${API_BASE_URL}/agents/?page=1&page_size=25&ordering=name`,
    examples: buildAuthenticatedGetExamples(`${API_BASE_URL}/agents/?page=1&page_size=25&ordering=name`),
    responses: [
      {
        status: 200,
        body: [
          '{',
          '  "count": 1,',
          '  "next": null,',
          '  "previous": null,',
          '  "results": [',
          '    {',
          '      "id": "f08c1745-2f11-4fb4-aab1-d120fb7251ce",',
          '      "name": "Bia Comercial",',
          '      "variables": ["city", "first_name"],',
          '      "transfer_target_required": true,',
          '      "requires_pre_call_script": true',
          '    }',
          '  ]',
          '}',
        ].join('\n'),
      },
      {
        status: 403,
        body: ['{', '  "detail": "You do not have permission to perform this action."', '}'].join('\n'),
      },
    ],
  },
  scriptsList: {
    method: 'GET',
    path: '/public/v1/scripts/',
    fullUrl: `${API_BASE_URL}/scripts/?page=1&page_size=25&ordering=name`,
    examples: buildAuthenticatedGetExamples(`${API_BASE_URL}/scripts/?page=1&page_size=25&ordering=name`),
    responses: [
      {
        status: 200,
        body: [
          '{',
          '  "count": 2,',
          '  "next": null,',
          '  "previous": null,',
          '  "results": [',
          '    {',
          '      "id": "a1a97f62-9dc1-4ef4-8f2b-dbbd6d86c0b4",',
          '      "name": "Enviar webhook de inicio",',
          '      "description": "Notifica o CRM quando a chamada inicia.",',
          '      "event_type": "call_started",',
          '      "script_type": "code",',
          '      "updated_at": "2026-05-27T18:00:00Z"',
          '    },',
          '    {',
          '      "id": "d2c6a14e-77f9-46df-917c-53bd69c39701",',
          '      "name": "Validar elegibilidade antes da ligação",',
          '      "description": "Autoriza, adia ou bloqueia a chamada antes da criação.",',
          '      "event_type": "pre_call",',
          '      "script_type": "code",',
          '      "updated_at": "2026-05-27T18:05:00Z"',
          '    }',
          '  ]',
          '}',
        ].join('\n'),
      },
    ],
  },
  usersList: {
    method: 'GET',
    path: '/public/v1/users/',
    fullUrl: `${API_BASE_URL}/users/?page=1&page_size=25&ordering=email`,
    examples: buildAuthenticatedGetExamples(`${API_BASE_URL}/users/?page=1&page_size=25&ordering=email`),
    responses: [
      {
        status: 200,
        body: [
          '{',
          '  "count": 1,',
          '  "next": null,',
          '  "previous": null,',
          '  "results": [',
          '    {',
          '      "id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a",',
          '      "name": "Mariana Operacoes",',
          '      "email": "mariana@example.com",',
          '      "is_active": true,',
          '      "transfer_eligible": true,',
          '      "telephony_endpoint": {',
          '        "id": "7bd9ca42-9445-4f15-9d3e-46f7f29cc7e7",',
          '        "endpoint_type": "internal_extension",',
          '        "extension": "4010",',
          '        "external_phone_e164": null',
          '      },',
          '      "last_login_at": "2026-05-27T16:45:00Z",',
          '      "updated_at": "2026-05-27T16:45:00Z"',
          '    }',
          '  ]',
          '}',
        ].join('\n'),
      },
    ],
  },
  transferGroupsList: {
    method: 'GET',
    path: '/public/v1/transfer-groups/',
    fullUrl: `${API_BASE_URL}/transfer-groups/?page=1&page_size=25&ordering=name`,
    examples: buildAuthenticatedGetExamples(`${API_BASE_URL}/transfer-groups/?page=1&page_size=25&ordering=name`),
    responses: [
      {
        status: 200,
        body: [
          '{',
          '  "count": 1,',
          '  "next": null,',
          '  "previous": null,',
          '  "results": [',
          '    {',
          '      "id": "2c31d73a-d927-47b8-a019-273e3abf582c",',
          '      "name": "Time Comercial",',
          '      "is_active": true,',
          '      "transfer_eligible": true,',
          '      "strategy": "rrmemory",',
          '      "eligible_members_count": 4,',
          '      "updated_at": "2026-05-27T17:10:00Z"',
          '    }',
          '  ]',
          '}',
        ].join('\n'),
      },
    ],
  },
};
